import { useState, useEffect, useCallback } from "react";

// ── THEMES ────────────────────────────────────────────────────
const DARK = {
  bg:"#0D0F14", surface:"#13161D", card:"#181C25", border:"#1E2433",
  accent:"#00D4AA", accent2:"#0099FF", text:"#E8EBF0", muted:"#5A6478",
  dim:"#2A3044", green:"#00C17C", red:"#FF4D6A", amber:"#FFB020",
  white:"#FFFFFF", inputBg:"#13161D", isDark:true,
};
const LIGHT = {
  bg:"#F4F6F9", surface:"#FFFFFF", card:"#FFFFFF", border:"#E2E8F0",
  accent:"#00B894", accent2:"#0099FF", text:"#1A202C", muted:"#718096",
  dim:"#EDF2F7", green:"#00C17C", red:"#E53E3E", amber:"#D69E2E",
  white:"#FFFFFF", inputBg:"#FFFFFF", isDark:false,
};

// ── STORAGE — works inside Claude AND on any website ──────────
const KEYS = { invoices:"invoiceai5:inv", contacts:"invoiceai5:con", settings:"invoiceai5:sett", theme:"invoiceai5:theme", apikey:"invoiceai5:apikey" };
const store = {
  get: async (k) => {
    try {
      // Try Claude artifact storage first
      if (typeof window.storage !== "undefined") {
        const r = await window.storage.get(k);
        return r ? JSON.parse(r.value) : null;
      }
    } catch {}
    // Fall back to localStorage (works on any website)
    try {
      const v = localStorage.getItem(k);
      return v ? JSON.parse(v) : null;
    } catch { return null; }
  },
  set: async (k, v) => {
    const data = JSON.stringify(v);
    try {
      if (typeof window.storage !== "undefined") {
        await window.storage.set(k, data); return;
      }
    } catch {}
    try { localStorage.setItem(k, data); } catch {}
  },
};

// ── HELPERS ───────────────────────────────────────────────────
const uid     = () => `${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
const invNo   = () => "INV-"+Math.floor(100000+Math.random()*900000);
const qNo     = () => "QUO-"+Math.floor(100000+Math.random()*900000);
const rcNo    = () => "RCP-"+Math.floor(100000+Math.random()*900000);
const today   = () => new Date().toISOString().split("T")[0];
const addDays = (n) => { const d=new Date(); d.setDate(d.getDate()+n); return d.toISOString().split("T")[0]; };
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}) : "—";
const fmtNum  = (n,sym,locale) => { const num=isNaN(n)?0:Number(n); return sym+num.toLocaleString(locale,{minimumFractionDigits:2,maximumFractionDigits:2}); };
const clamp   = (n) => Math.max(0, parseFloat(n)||0);
const invFmt  = (inv) => { const s=inv.currency==="NGN"?"₦":"$"; const l=inv.currency==="NGN"?"en-NG":"en-US"; return (n)=>fmtNum(n,s,l); };

// ── API CALL — uses stored key on website, auto in Claude ─────
const callClaude = async (prompt, apiKey, max_tokens=1000) => {
  const headers = { "Content-Type":"application/json", "anthropic-version":"2023-06-01" };
  // Inside Claude artifacts, we can use direct browser access
  // On a website, the user provides their own API key
  if (!apiKey) headers["anthropic-dangerous-direct-browser-access"] = "true";
  else headers["x-api-key"] = apiKey;

  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method:"POST", headers,
    body: JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens, messages:[{role:"user",content:prompt}] }),
  });
  const data = await res.json();
  if(!res.ok) throw new Error(data.error?.message||"API error "+res.status);
  return data.content?.[0]?.text||"";
};
const parseJ = (raw) => JSON.parse(raw.replace(/```json|```/g,"").trim());

// ── PRINT WINDOW ──────────────────────────────────────────────
const openPrintWindow = (inv, sett) => {
  const f = invFmt(inv);
  const paid = (inv.payments||[]).reduce((s,p)=>s+p.amount,0);
  const bal  = Math.max(0,(inv.total||0)-paid);
  const isReceipt = inv.type==="receipt";
  const rows = (inv.items||[]).filter(i=>i.desc?.trim()).map(it=>`
    <tr>
      <td style="padding:8px 4px;border-bottom:1px solid #eee;font-size:13px;">${it.desc||""}</td>
      <td style="padding:8px 4px;border-bottom:1px solid #eee;font-size:13px;text-align:center;">${it.qty||1}</td>
      <td style="padding:8px 4px;border-bottom:1px solid #eee;font-size:13px;text-align:right;">${f(clamp(it.rate))}</td>
      <td style="padding:8px 4px;border-bottom:1px solid #eee;font-size:13px;text-align:right;font-weight:600;">${f(clamp(it.qty)*clamp(it.rate))}</td>
    </tr>`).join("");

  const termHTML = isReceipt
    ? `<div style="font-size:12px;font-weight:700;color:#00C17C;">✅ Payment Received</div>`
    : inv.payTerms==="due_receipt"   ? `<div style="font-size:12px;font-weight:700;color:#e53e3e;">⚡ Due on Receipt</div>`
    : inv.payTerms==="deposit_50"    ? `<div style="font-size:12px;font-weight:700;color:#d69e2e;">50% Deposit Required</div>`
    : inv.payTerms==="full_upfront"  ? `<div style="font-size:12px;font-weight:700;color:#d69e2e;">Full Payment Upfront</div>`
    : inv.payTerms==="installment2"  ? `<div style="font-size:12px;font-weight:700;color:#0099ff;">2x Installment · Due: ${fmtDate(inv.dueDate)}</div>`
    : `<div style="font-size:12px;color:#888;">Due: ${fmtDate(inv.dueDate)}</div>`;

  const paidStamp = isReceipt
    ? `<div style="position:absolute;top:32px;right:40px;border:3px solid #00C17C;border-radius:6px;padding:4px 14px;color:#00C17C;font-weight:900;font-size:18px;transform:rotate(12deg);opacity:0.8;letter-spacing:2px;">PAID</div>`
    : "";

  // FIX: Receipt hides "Pay To" — payment already received
  const payToSection = (!isReceipt && sett.bankAccount) ? `
    <div style="background:#f8fafc;border-radius:8px;padding:12px;">
      <div style="font-size:10px;font-weight:700;color:#999;text-transform:uppercase;letter-spacing:1px;margin-bottom:7px;">Pay To</div>
      <div style="font-weight:700;font-size:13px;">${sett.bankName||""}</div>
      <div style="font-size:11px;color:#666;">${sett.bankAccount}</div>
      ${sett.bankBranch?`<div style="font-size:11px;color:#666;">${sett.bankBranch}</div>`:""}
    </div>` : "";

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${inv.docNo||"Document"}</title>
<style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:'Segoe UI',Arial,sans-serif;background:#fff;color:#1a1a2e;padding:32px;}@media print{.np{display:none!important;}@page{margin:12mm;}}</style>
</head><body>
<div class="np" style="margin-bottom:20px;display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
  <button onclick="window.print()" style="background:#00B894;color:#fff;border:none;padding:10px 22px;border-radius:8px;font-weight:700;cursor:pointer;font-size:14px;">🖨️ Print / Save PDF</button>
  <button onclick="window.close()" style="background:#eee;border:none;padding:10px 16px;border-radius:8px;cursor:pointer;font-size:13px;">✕ Close</button>
  <span style="font-size:12px;color:#888;">Print dialog → Destination → "Save as PDF"</span>
</div>
<div style="max-width:680px;margin:0 auto;border:1px solid #e2e8f0;border-radius:10px;padding:36px;position:relative;">
  ${paidStamp}
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;">
    <div>
      <div style="font-size:20px;font-weight:900;margin-bottom:5px;">${sett.bizName||"Your Business"}</div>
      ${sett.bizAddress?`<div style="font-size:12px;color:#666;margin-bottom:2px;">${sett.bizAddress}</div>`:""}
      ${sett.bizPhone?`<div style="font-size:12px;color:#666;margin-bottom:2px;">${sett.bizPhone}</div>`:""}
      ${sett.bizEmail?`<div style="font-size:12px;color:#666;">${sett.bizEmail}</div>`:""}
    </div>
    <div style="text-align:right;">
      <div style="font-size:16px;font-weight:900;color:${isReceipt?"#00C17C":"#00B894"};text-transform:uppercase;letter-spacing:2px;">${inv.type||"invoice"}</div>
      <div style="font-size:13px;font-weight:700;margin-top:4px;">${inv.docNo||""}</div>
      <div style="font-size:11px;color:#888;margin-top:7px;">Date: ${fmtDate(inv.issueDate)}</div>
      ${termHTML}
    </div>
  </div>
  <div style="display:grid;grid-template-columns:${payToSection?"1fr 1fr":"1fr"};gap:14px;margin-bottom:24px;">
    <div style="background:#f8fafc;border-radius:8px;padding:12px;">
      <div style="font-size:10px;font-weight:700;color:#999;text-transform:uppercase;letter-spacing:1px;margin-bottom:7px;">${isReceipt?"Received From":"Bill To"}</div>
      <div style="font-weight:700;font-size:13px;">${inv.client?.name||"—"}</div>
      ${inv.client?.email?`<div style="font-size:11px;color:#666;">${inv.client.email}</div>`:""}
      ${inv.client?.phone?`<div style="font-size:11px;color:#666;">${inv.client.phone}</div>`:""}
    </div>
    ${payToSection}
  </div>
  <table style="width:100%;border-collapse:collapse;margin-bottom:18px;">
    <thead><tr style="border-bottom:2px solid #1a1a2e;">
      <th style="padding:7px 4px;font-size:11px;text-align:left;text-transform:uppercase;letter-spacing:1px;">Description</th>
      <th style="padding:7px 4px;font-size:11px;text-align:center;text-transform:uppercase;letter-spacing:1px;">Qty</th>
      <th style="padding:7px 4px;font-size:11px;text-align:right;text-transform:uppercase;letter-spacing:1px;">Rate</th>
      <th style="padding:7px 4px;font-size:11px;text-align:right;text-transform:uppercase;letter-spacing:1px;">Amount</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;">
    <div style="width:210px;">
      <div style="display:flex;justify-content:space-between;font-size:12px;color:#666;margin-bottom:4px;"><span>Subtotal</span><span>${f(inv.subtotal||0)}</span></div>
      ${(inv.discount||0)>0?`<div style="display:flex;justify-content:space-between;font-size:12px;color:#00C17C;margin-bottom:4px;"><span>Discount (${inv.discount}%)</span><span>-${f(inv.discountAmt||0)}</span></div>`:""}
      <div style="display:flex;justify-content:space-between;font-size:12px;color:#666;margin-bottom:4px;"><span>Tax (${inv.taxRate||0}%)</span><span>${f(inv.taxAmt||0)}</span></div>
      <div style="display:flex;justify-content:space-between;font-size:15px;font-weight:900;border-top:2px solid #1a1a2e;padding-top:7px;margin-top:5px;"><span>TOTAL</span><span style="color:#00B894;">${f(inv.total||0)}</span></div>
      ${paid>0?`<div style="margin-top:7px;padding-top:7px;border-top:1px solid #eee;">
        <div style="display:flex;justify-content:space-between;font-size:11px;color:#00C17C;margin-bottom:3px;"><span>Amount Paid</span><span>-${f(paid)}</span></div>
        <div style="display:flex;justify-content:space-between;font-size:12px;font-weight:800;color:#e53e3e;"><span>Balance Due</span><span>${f(bal)}</span></div>
      </div>`:""}
    </div>
  </div>
  ${inv.notes?`<div style="margin-top:18px;padding:10px 12px;background:#f8fafc;border-radius:7px;border-left:3px solid #00B894;"><div style="font-size:10px;font-weight:700;color:#999;text-transform:uppercase;margin-bottom:3px;">Notes</div><div style="font-size:12px;color:#444;line-height:1.6;">${inv.notes}</div></div>`:""}
  ${inv.paymentMethod?`<div style="margin-top:14px;font-size:12px;color:#888;">Payment Method: <strong>${inv.paymentMethod}</strong></div>`:""}
  <div style="margin-top:22px;text-align:center;font-size:11px;color:#ccc;">Generated by InvoiceAI Pro · JERRYTECH Global Communications</div>
</div></body></html>`;

  try {
    const win = window.open("","_blank","width=820,height=720,noopener");
    if(win && !win.closed){ win.document.write(html); win.document.close(); return; }
  } catch(e){}
  // Fallback: Blob data URI (works when popups blocked)
  const blob = new Blob([html],{type:"text/html"});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href=url; a.target="_blank"; a.rel="noopener";
  document.body.appendChild(a); a.click();
  setTimeout(()=>{ URL.revokeObjectURL(url); document.body.removeChild(a); },1000);
};

// ── CONSTANTS ─────────────────────────────────────────────────
const STATUS = {
  draft:   {label:"Draft",   color:"#5A6478", bg:"#1E243388"},
  sent:    {label:"Sent",    color:"#0099FF", bg:"#0099FF18"},
  partial: {label:"Partial", color:"#FFB020", bg:"#FFB02018"},
  paid:    {label:"Paid",    color:"#00C17C", bg:"#00C17C18"},
  overdue: {label:"Overdue", color:"#FF4D6A", bg:"#FF4D6A18"},
};
const TERMS = [
  {id:"due_receipt",  label:"Due on Receipt",  days:0,  note:"Full payment when collected"},
  {id:"deposit_50",   label:"50% Deposit",      days:0,  note:"Half upfront, half on collection"},
  {id:"full_upfront", label:"Full Upfront",      days:0,  note:"Full payment before work starts"},
  {id:"installment2", label:"2x Installment",    days:14, note:"Split into 2 equal payments"},
  {id:"net7",         label:"Net 7 days",        days:7,  note:"Payment within 7 days"},
  {id:"net14",        label:"Net 14 days",       days:14, note:"Payment within 14 days"},
  {id:"net30",        label:"Net 30 days",       days:30, note:"Payment within 30 days"},
];
const PAY_METHODS = ["Cash","Bank Transfer","Card","POS","Cheque","Mobile Money","Crypto","Other"];

// ── COMPONENTS ────────────────────────────────────────────────
function Tag({status}) {
  const s=STATUS[status]||STATUS.draft;
  return <span style={{fontSize:"0.58rem",fontWeight:700,background:s.bg,color:s.color,padding:"2px 8px",borderRadius:10}}>{s.label}</span>;
}
function Dots({text,C}) {
  return (
    <div style={{textAlign:"center",padding:"20px 0"}}>
      <div style={{display:"flex",justifyContent:"center",gap:6,marginBottom:8}}>
        {[0,1,2].map(i=><div key={i} style={{width:7,height:7,borderRadius:"50%",background:C.accent,animation:`bounce 1.2s ${i*0.2}s infinite`}}/>)}
      </div>
      <div style={{fontSize:"0.7rem",color:C.muted}}>{text}</div>
    </div>
  );
}
function ErrBox({msg,C}){
  return <div style={{marginTop:10,background:C.isDark?"#2a1010":"#fff5f5",border:"1px solid "+C.red+"44",borderRadius:8,padding:"10px 14px",color:C.red,fontSize:"0.74rem"}}>⚠️ {msg}</div>;
}
// FIX: added onClose to dependency array
function Toast({msg,C,onClose}){
  useEffect(()=>{ const t=setTimeout(onClose,2800); return()=>clearTimeout(t); },[onClose]);
  return <div style={{position:"fixed",bottom:24,right:24,background:C.accent,color:"#000",padding:"10px 18px",borderRadius:10,fontWeight:700,fontSize:"0.8rem",zIndex:9999,boxShadow:"0 4px 20px "+C.accent+"44"}}>{msg}</div>;
}

function ConfirmModal({item, onConfirm, onCancel, C}) {
  if(!item) return null;
  const isInv = item.type==="invoice";
  return (
    <div style={{position:"fixed",inset:0,background:"#00000088",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:16,padding:24,maxWidth:340,width:"100%",boxShadow:"0 20px 60px #00000066"}}>
        <div style={{fontSize:"1.4rem",marginBottom:12,textAlign:"center"}}>🗑️</div>
        <div style={{fontWeight:700,fontSize:"0.9rem",color:C.text,marginBottom:8,textAlign:"center"}}>Delete this {isInv?"invoice":"contact"}?</div>
        <div style={{fontSize:"0.76rem",color:C.muted,textAlign:"center",marginBottom:20}}>This cannot be undone.</div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={onCancel} style={{flex:1,padding:"10px",borderRadius:9,border:"1px solid "+C.border,background:"transparent",color:C.muted,fontFamily:"inherit",fontSize:"0.8rem",fontWeight:700,cursor:"pointer"}}>Cancel</button>
          <button onClick={onConfirm} style={{flex:1,padding:"10px",borderRadius:9,border:"none",background:C.red,color:"#fff",fontFamily:"inherit",fontSize:"0.8rem",fontWeight:700,cursor:"pointer"}}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── INVOICE PREVIEW ───────────────────────────────────────────
function InvPreview({inv, sett, C}) {
  if(!inv) return null;
  const f = invFmt(inv);
  const paid = (inv.payments||[]).reduce((s,p)=>s+p.amount,0);
  const bal  = Math.max(0,(inv.total||0)-paid);
  const isReceipt = inv.type==="receipt";

  return (
    <div style={{background:"#fff",borderRadius:14,padding:28,color:"#1a1a2e",border:"1px solid #e2e8f0",position:"relative"}}>
      {isReceipt&&(
        <div style={{position:"absolute",top:28,right:28,border:"3px solid #00C17C",borderRadius:8,padding:"4px 12px",color:"#00C17C",fontWeight:900,fontSize:"1.1rem",transform:"rotate(12deg)",opacity:0.85,letterSpacing:"0.1em"}}>PAID</div>
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
        <div>
          <div style={{fontSize:"1.3rem",fontWeight:900,marginBottom:4}}>{sett.bizName||"Your Business"}</div>
          {sett.bizAddress&&<div style={{fontSize:"0.72rem",color:"#666",marginBottom:2}}>{sett.bizAddress}</div>}
          {sett.bizPhone&&<div style={{fontSize:"0.72rem",color:"#666",marginBottom:2}}>{sett.bizPhone}</div>}
          {sett.bizEmail&&<div style={{fontSize:"0.72rem",color:"#666"}}>{sett.bizEmail}</div>}
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:"0.9rem",fontWeight:900,color:isReceipt?"#00C17C":"#00B894",textTransform:"uppercase",letterSpacing:"0.1em"}}>{inv.type||"invoice"}</div>
          <div style={{fontSize:"0.78rem",fontWeight:700,marginTop:2}}>{inv.docNo}</div>
          <div style={{fontSize:"0.66rem",color:"#888",marginTop:6}}>Date: {fmtDate(inv.issueDate)}</div>
          {isReceipt
            ? <div style={{fontSize:"0.66rem",fontWeight:700,color:"#00C17C"}}>✅ Payment Received</div>
            : inv.payTerms==="due_receipt"   ? <div style={{fontSize:"0.66rem",fontWeight:700,color:"#e53e3e"}}>⚡ Due on Receipt</div>
            : inv.payTerms==="deposit_50"    ? <div style={{fontSize:"0.66rem",fontWeight:700,color:"#d69e2e"}}>50% Deposit Required</div>
            : inv.payTerms==="full_upfront"  ? <div style={{fontSize:"0.66rem",fontWeight:700,color:"#d69e2e"}}>Full Payment Upfront</div>
            : inv.payTerms==="installment2"  ? <div style={{fontSize:"0.66rem",fontWeight:700,color:"#0099ff"}}>2x Installment · Due: {fmtDate(inv.dueDate)}</div>
            : <div style={{fontSize:"0.66rem",color:"#888"}}>Due: {fmtDate(inv.dueDate)}</div>
          }
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:(!isReceipt&&sett.bankAccount)?"1fr 1fr":"1fr",gap:12,marginBottom:20}}>
        <div style={{background:"#f8fafc",borderRadius:8,padding:"12px 14px"}}>
          <div style={{fontSize:"0.54rem",fontWeight:700,color:"#999",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:5}}>{isReceipt?"Received From":"Bill To"}</div>
          <div style={{fontWeight:700,fontSize:"0.86rem"}}>{inv.client?.name||"—"}</div>
          {inv.client?.email&&<div style={{fontSize:"0.7rem",color:"#666"}}>{inv.client.email}</div>}
          {inv.client?.phone&&<div style={{fontSize:"0.7rem",color:"#666"}}>{inv.client.phone}</div>}
        </div>
        {/* FIX: Receipt hides Pay To */}
        {!isReceipt&&sett.bankAccount&&(
          <div style={{background:"#f8fafc",borderRadius:8,padding:"12px 14px"}}>
            <div style={{fontSize:"0.54rem",fontWeight:700,color:"#999",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:5}}>Pay To</div>
            <div style={{fontWeight:700,fontSize:"0.78rem"}}>{sett.bankName}</div>
            <div style={{fontSize:"0.7rem",color:"#666"}}>{sett.bankAccount}</div>
            {sett.bankBranch&&<div style={{fontSize:"0.68rem",color:"#666"}}>{sett.bankBranch}</div>}
          </div>
        )}
      </div>

      <div style={{marginBottom:16}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 44px 84px 84px",gap:8,padding:"7px 0",borderBottom:"2px solid #1a1a2e",marginBottom:5}}>
          {["Description","Qty","Rate","Amount"].map((h,i)=><div key={i} style={{fontSize:"0.56rem",fontWeight:800,textTransform:"uppercase",letterSpacing:"0.06em",textAlign:i>1?"right":"left"}}>{h}</div>)}
        </div>
        {(inv.items||[]).filter(i=>i.desc?.trim()).map((item,idx)=>(
          <div key={idx} style={{display:"grid",gridTemplateColumns:"1fr 44px 84px 84px",gap:8,padding:"7px 0",borderBottom:"1px solid #f0f0f0"}}>
            <div style={{fontSize:"0.78rem"}}>{item.desc}</div>
            <div style={{fontSize:"0.78rem",color:"#666",textAlign:"center"}}>{item.qty}</div>
            <div style={{fontSize:"0.78rem",color:"#666",textAlign:"right"}}>{f(clamp(item.rate))}</div>
            <div style={{fontSize:"0.78rem",fontWeight:600,textAlign:"right"}}>{f(clamp(item.qty)*clamp(item.rate))}</div>
          </div>
        ))}
      </div>

      <div style={{display:"flex",justifyContent:"flex-end"}}>
        <div style={{width:200}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:"0.72rem",color:"#666",marginBottom:4}}><span>Subtotal</span><span>{f(inv.subtotal||0)}</span></div>
          {(inv.discount||0)>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:"0.72rem",color:"#00C17C",marginBottom:4}}><span>Discount ({inv.discount}%)</span><span>-{f(inv.discountAmt||0)}</span></div>}
          <div style={{display:"flex",justifyContent:"space-between",fontSize:"0.72rem",color:"#666",marginBottom:4}}><span>Tax ({inv.taxRate||0}%)</span><span>{f(inv.taxAmt||0)}</span></div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:"0.9rem",fontWeight:900,borderTop:"2px solid #1a1a2e",paddingTop:6,marginTop:4}}>
            <span>TOTAL</span><span style={{color:"#00B894"}}>{f(inv.total||0)}</span>
          </div>
          {paid>0&&<div style={{marginTop:6,paddingTop:6,borderTop:"1px solid #eee"}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:"0.7rem",color:"#00C17C",marginBottom:2}}><span>Paid</span><span>-{f(paid)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:"0.76rem",fontWeight:800,color:"#e53e3e"}}><span>Balance Due</span><span>{f(bal)}</span></div>
          </div>}
        </div>
      </div>
      {inv.paymentMethod&&<div style={{marginTop:14,fontSize:"0.72rem",color:"#888"}}>Payment Method: <strong style={{color:"#1a1a2e"}}>{inv.paymentMethod}</strong></div>}
      {inv.notes&&<div style={{marginTop:16,padding:"10px 12px",background:"#f8fafc",borderRadius:7,borderLeft:"3px solid #00B894"}}>
        <div style={{fontSize:"0.54rem",fontWeight:700,color:"#999",textTransform:"uppercase",marginBottom:3}}>Notes</div>
        <div style={{fontSize:"0.72rem",color:"#444",lineHeight:1.6}}>{inv.notes}</div>
      </div>}
      <div style={{marginTop:18,textAlign:"center",fontSize:"0.6rem",color:"#ccc"}}>Generated by InvoiceAI Pro · JERRYTECH Global Communications</div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────
export default function InvoiceAIPro() {
  const [isDark,   setIsDark]   = useState(true);
  const [tab,      setTab]      = useState("new");
  const [loading,  setLoading]  = useState(true);
  const [toast,    setToast]    = useState(null);
  const [apiKey,   setApiKey]   = useState("");
  const C = isDark ? DARK : LIGHT;
  const showToast   = useCallback((msg) => setToast(msg), []);
  const closeToast  = useCallback(() => setToast(null), []);

  const inp  = (x={}) => ({width:"100%",background:C.inputBg,border:"1px solid "+C.border,borderRadius:8,padding:"9px 12px",fontFamily:"inherit",fontSize:"0.82rem",color:C.text,outline:"none",boxSizing:"border-box",...x});
  const SLbl = {fontSize:"0.6rem",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:C.muted,marginBottom:5,display:"block"};
  const Card = {background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:16,marginBottom:14};
  const tabS = (id) => ({padding:"8px 13px",border:"none",borderRadius:7,fontFamily:"inherit",fontSize:"0.7rem",fontWeight:700,cursor:"pointer",background:tab===id?C.accent:"transparent",color:tab===id?"#000":C.muted});
  const solidBtn = (bg,col="white",extra={}) => ({padding:"11px",borderRadius:9,border:"none",background:bg,color:col,fontFamily:"inherit",fontSize:"0.78rem",fontWeight:700,cursor:"pointer",...extra});
  const outlineBtn = (col) => ({padding:"11px",borderRadius:9,border:"1px solid "+col,background:"transparent",color:col,fontFamily:"inherit",fontSize:"0.78rem",fontWeight:700,cursor:"pointer"});

  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState({bizName:"",bizEmail:"",bizPhone:"",bizAddress:"",bankName:"",bankAccount:"",bankBranch:"",taxRate:7.5,currency:"NGN"});

  useEffect(()=>{
    (async()=>{
      const [inv,con,sett,thm,key]=await Promise.all([
        store.get(KEYS.invoices),store.get(KEYS.contacts),store.get(KEYS.settings),
        store.get(KEYS.theme),store.get(KEYS.apikey),
      ]);
      if(inv) setInvoices(inv);
      if(con) setContacts(con);
      if(sett) setSettings(s=>({...s,...sett}));
      if(thm!==null) setIsDark(thm);
      if(key) setApiKey(key);
      setLoading(false);
    })();
  },[]);

  const saveInvoices = async (d)=>{ setInvoices(d); await store.set(KEYS.invoices,d); };
  const saveContacts = async (d)=>{ setContacts(d); await store.set(KEYS.contacts,d); };
  const saveSettings = async (d)=>{ setSettings(d); await store.set(KEYS.settings,d); };
  const toggleTheme  = async ()=>{ const n=!isDark; setIsDark(n); await store.set(KEYS.theme,n); };
  const saveApiKey   = async (k)=>{ setApiKey(k); await store.set(KEYS.apikey,k); };

  // ── Form ──
  const [currency,      setCurrency]      = useState("NGN");
  const [docType,       setDocType]       = useState("invoice");
  const [docNo,         setDocNo]         = useState(invNo());
  const [issueDate,     setIssueDate]     = useState(today());
  const [payTerms,      setPayTerms]      = useState("due_receipt");
  const [dueDate,       setDueDate]       = useState(today());
  const [clientName,    setClientName]    = useState("");
  const [clientEmail,   setClientEmail]   = useState("");
  const [clientPhone,   setClientPhone]   = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [items,         setItems]         = useState([{id:uid(),desc:"",qty:1,rate:""}]);
  const [discount,      setDiscount]      = useState(0);
  const [notes,         setNotes]         = useState("");
  const [aiJob,         setAiJob]         = useState("");
  const [aiLoading,     setAiLoading]     = useState(false);
  const [aiError,       setAiError]       = useState("");
  const [newStep,       setNewStep]       = useState("form");
  const [savedInv,      setSavedInv]      = useState(null);
  const [previewInv,    setPreviewInv]    = useState(null);
  const [selContact,    setSelContact]    = useState(null);
  const [nameQuery,     setNameQuery]     = useState("");
  const [showSug,       setShowSug]       = useState(false);

  const contactSug = nameQuery.trim().length>=1
    ? contacts.filter(c=>c.name.toLowerCase().includes(nameQuery.toLowerCase())).slice(0,5)
    : [];

  const sym    = currency==="NGN"?"₦":"$";
  const locale = currency==="NGN"?"en-NG":"en-US";
  const f      = (n)=>fmtNum(n,sym,locale);

  const subtotal    = items.reduce((s,i)=>s+clamp(i.qty)*clamp(i.rate),0);
  const discountAmt = subtotal*((parseFloat(discount)||0)/100);
  const taxAmt      = (subtotal-discountAmt)*((settings.taxRate||0)/100);
  const total       = subtotal-discountAmt+taxAmt;

  const addItem    = ()=>setItems(p=>[...p,{id:uid(),desc:"",qty:1,rate:""}]);
  const removeItem = (id)=>{ if(items.length<=1) return; setItems(p=>p.filter(i=>i.id!==id)); };
  const updItem    = (id,field,val)=>setItems(p=>p.map(i=>i.id===id?{...i,[field]:val}:i));
  const pickContact= (c)=>{ setSelContact(c); setClientName(c.name); setNameQuery(c.name); setClientEmail(c.email||""); setClientPhone(c.phone||""); };
  const changeTerm = (tid)=>{ setPayTerms(tid); const t=TERMS.find(t=>t.id===tid); if(t) setDueDate(addDays(t.days)); };
  const changeDocType=(type)=>{ setDocType(type); setDocNo(type==="invoice"?invNo():type==="quote"?qNo():rcNo()); if(type==="receipt") setPayTerms("due_receipt"); };

  const handleNameChange = (val)=>{
    setNameQuery(val); setClientName(val); setShowSug(true);
    if(!val.trim()){ setClientEmail(""); setClientPhone(""); setSelContact(null); }
  };
  const selectSug = (c)=>{ pickContact(c); setShowSug(false); };

  const resetForNew = ()=>{
    setDocNo(invNo()); setDocType("invoice"); setIssueDate(today());
    setPayTerms("due_receipt"); setDueDate(today());
    setClientName(""); setClientEmail(""); setClientPhone("");
    setNameQuery(""); setShowSug(false); setPaymentMethod("Cash");
    setItems([{id:uid(),desc:"",qty:1,rate:""}]);
    setDiscount(0); setNotes(""); setAiJob(""); setAiError("");
    setSelContact(null); setSavedInv(null); setPreviewInv(null); setNewStep("form");
  };
  const handleTabClick = (id)=>{ if(id==="new") resetForNew(); setTab(id); };

  const generateFromAI = async ()=>{
    if(!aiJob.trim()){ setAiError("Describe the job first."); return; }
    setAiError(""); setAiLoading(true);
    try {
      const raw=await callClaude(
        `Pricing consultant for service businesses in ${currency==="NGN"?"Nigeria (Naira, Lagos market rates)":"USD (US rates)"}.\nBusiness: ${settings.bizName||"service business"}\nJob: "${aiJob}"\nReturn ONLY valid JSON, no markdown:\n{"items":[{"desc":"item name","qty":1,"rate":5000}],"notes":"warranty or payment note"}`,
        apiKey
      );
      const r=parseJ(raw);
      setItems(r.items.map(it=>({id:uid(),...it})));
      if(r.notes) setNotes(r.notes);
    } catch(e){ setAiError(e.message||"Something went wrong."); }
    setAiLoading(false);
  };

  const buildInvObj = (status) => ({
    id:uid(), docNo, type:docType, status, currency,
    issueDate, dueDate, payTerms, paymentMethod,
    client:{name:clientName,email:clientEmail,phone:clientPhone},
    items:items.filter(i=>i.desc.trim()),
    discount:parseFloat(discount)||0, taxRate:settings.taxRate,
    notes, subtotal, discountAmt, taxAmt, total,
    payments:[], createdAt:Date.now(),
  });

  const goToPreview = ()=>{
    const inv=buildInvObj("draft");
    if(!inv.items.length){ showToast("Add at least one item first."); return; }
    setPreviewInv(inv); setNewStep("preview");
  };

  const updateContactOnSave = async (inv)=>{
    if(!clientName.trim()) return;
    const ex=contacts.find(c=>c.name.toLowerCase()===clientName.toLowerCase());
    if(ex){
      await saveContacts(contacts.map(c=>c.id===ex.id?{...c,email:clientEmail||c.email,phone:clientPhone||c.phone,totalInvoiced:(c.totalInvoiced||0)+inv.total,invoiceCount:(c.invoiceCount||0)+1,lastInvoice:today()}:c));
    } else {
      await saveContacts([{id:uid(),name:clientName,email:clientEmail,phone:clientPhone,address:"",totalInvoiced:inv.total,invoiceCount:1,lastInvoice:today()},...contacts]);
    }
  };

  const handleSaveAndSend = async ()=>{
    const inv={...(previewInv||buildInvObj("sent")), id:uid(), status:"sent"};
    await saveInvoices([inv,...invoices]); await updateContactOnSave(inv);
    setSavedInv(inv); setNewStep("success"); showToast("Invoice saved!");
  };

  const handleSaveAndPaid = async ()=>{
    const inv={...(previewInv||buildInvObj("paid")), id:uid(), status:"paid"};
    const invP={...inv, payments:[{id:uid(),date:today(),amount:inv.total,method:paymentMethod,note:"Paid in full on issue"}]};
    await saveInvoices([invP,...invoices]); await updateContactOnSave(invP);
    setSavedInv(invP); setNewStep("success"); showToast("Saved & marked Paid! 💚");
  };

  const handleSaveDraft = async ()=>{
    const inv=buildInvObj("draft");
    await saveInvoices([inv,...invoices]);
    setSavedInv(inv); setNewStep("success"); showToast("Draft saved!");
  };

  // FIX: success screen uses invoice's own currency formatter
  const savedF = savedInv ? invFmt(savedInv) : f;

  const copyWhatsApp = (inv)=>{
    const fi=invFmt(inv);
    const isReceipt=inv.type==="receipt";
    const termLine = isReceipt?"*✅ PAYMENT RECEIVED — Thank you!*"
      : inv.payTerms==="due_receipt"   ? "*Payment: Due on Receipt*"
      : inv.payTerms==="deposit_50"    ? "*Payment: 50% Deposit Required*"
      : inv.payTerms==="full_upfront"  ? "*Payment: Full Payment Upfront*"
      : inv.payTerms==="installment2"  ? `*Payment: 2x Installment — Due: ${fmtDate(inv.dueDate)}*`
      : `*Due: ${fmtDate(inv.dueDate)}*`;
    const lines=(inv.items||[]).filter(i=>i.desc?.trim()).map(i=>`• ${i.desc} (x${i.qty}) — ${fi(clamp(i.qty)*clamp(i.rate))}`).join("\n");
    const text=[
      `*${(inv.type||"invoice").toUpperCase()} ${inv.docNo}*`,
      `*${settings.bizName||"Business"}*`,
      settings.bizPhone?"📞 "+settings.bizPhone:"",
      "",
      `*${isReceipt?"Received From":"Bill To"}:* ${inv.client?.name||"—"}`,
      `*Date:* ${fmtDate(inv.issueDate)}`,
      termLine,"","*ITEMS:*",lines,"",
      `*Subtotal:* ${fi(inv.subtotal||0)}`,
      (inv.discount||0)>0?`*Discount (${inv.discount}%):* -${fi(inv.discountAmt||0)}`:"",
      `*Tax (${inv.taxRate||0}%):* ${fi(inv.taxAmt||0)}`,
      `*TOTAL: ${fi(inv.total||0)}*`,
      (!isReceipt&&settings.bankAccount)?`\n🏦 *Pay to:* ${settings.bankName} — ${settings.bankAccount}`:"",
      inv.paymentMethod?`💳 *Method:* ${inv.paymentMethod}`:"",
      inv.notes?`\n📝 ${inv.notes}`:"",
    ].filter(l=>l!=="").join("\n");

    const doIt=(t)=>{
      if(navigator.clipboard?.writeText){
        navigator.clipboard.writeText(t).then(()=>showToast("Copied! 📱")).catch(()=>fallback(t));
      } else fallback(t);
    };
    const fallback=(t)=>{
      const ta=document.createElement("textarea");
      ta.value=t; ta.style.position="fixed"; ta.style.opacity="0";
      document.body.appendChild(ta); ta.select();
      try{ document.execCommand("copy"); showToast("Copied! 📱"); } catch{ showToast("Copy failed"); }
      document.body.removeChild(ta);
    };
    doIt(text);
  };

  // ── Records ──
  const [recFilter,   setRecFilter]   = useState("all");
  const [recSearch,   setRecSearch]   = useState("");
  const [expandedInv, setExpandedInv] = useState(null);
  const [viewingInv,  setViewingInv]  = useState(null);
  const [payAmt,      setPayAmt]      = useState("");
  const [payMeth,     setPayMeth]     = useState("Cash");
  const [payNote,     setPayNote]     = useState("");
  const [confirmDel,  setConfirmDel]  = useState(null);

  const filteredInv = invoices.filter(inv=>{
    const mf=recFilter==="all"||inv.status===recFilter;
    const ms=!recSearch||inv.docNo.toLowerCase().includes(recSearch.toLowerCase())||(inv.client?.name||"").toLowerCase().includes(recSearch.toLowerCase());
    return mf&&ms;
  });

  const updateStatus = async (invId,status)=>{ await saveInvoices(invoices.map(i=>i.id===invId?{...i,status}:i)); showToast("Status: "+STATUS[status]?.label); };

  const addPayment = async (invId)=>{
    const amt=parseFloat(payAmt);
    if(!amt||amt<=0){ showToast("Enter a valid amount"); return; }
    const inv=invoices.find(i=>i.id===invId); if(!inv) return;
    const payments=[...(inv.payments||[]),{id:uid(),date:today(),amount:amt,method:payMeth,note:payNote}];
    const paid=payments.reduce((s,p)=>s+p.amount,0);
    const ns=paid>=inv.total?"paid":paid>0?"partial":"sent";
    const updated=invoices.map(i=>i.id===invId?{...i,payments,status:ns}:i);
    await saveInvoices(updated);
    if(viewingInv?.id===invId) setViewingInv({...inv,payments,status:ns});
    setPayAmt(""); setPayNote("");
    showToast("Payment recorded! ✅");
  };

  const deleteInvoice = (invId)=>setConfirmDel({type:"invoice",id:invId});
  const doDeleteInvoice = async (invId)=>{ await saveInvoices(invoices.filter(i=>i.id!==invId)); setExpandedInv(null); setViewingInv(null); setConfirmDel(null); showToast("Deleted."); };

  // ── Contacts ──
  const [contactSearch, setContactSearch] = useState("");
  const [editContact,   setEditContact]   = useState(null);
  const [newContact,    setNewContact]    = useState({name:"",email:"",phone:"",address:""});
  const [addingContact, setAddingContact] = useState(false);

  const filteredContacts=contacts.filter(c=>!contactSearch||c.name.toLowerCase().includes(contactSearch.toLowerCase())||(c.phone||"").includes(contactSearch));
  const saveContact=async()=>{ if(!editContact?.name.trim()) return; await saveContacts(contacts.map(c=>c.id===editContact.id?editContact:c)); setEditContact(null); showToast("Saved!"); };
  const addContact=async()=>{ if(!newContact.name.trim()) return; await saveContacts([{id:uid(),...newContact,totalInvoiced:0,invoiceCount:0,lastInvoice:null},...contacts]); setNewContact({name:"",email:"",phone:"",address:""}); setAddingContact(false); showToast("Contact added!"); };
  const deleteContact=(id)=>setConfirmDel({type:"contact",id});
  const doDeleteContact=async(id)=>{ await saveContacts(contacts.filter(c=>c.id!==id)); setConfirmDel(null); showToast("Deleted."); };

  // ── Stats (currency-split) ──
  const statInv  = invoices.filter(i=>i.currency===currency);
  const fStat    = (n)=>fmtNum(n,sym,locale);
  const tInv     = statInv.reduce((s,i)=>s+(i.total||0),0);
  const tPaid    = statInv.reduce((s,i)=>s+(i.payments||[]).reduce((ss,p)=>ss+p.amount,0),0);
  const tOut     = tInv-tPaid;
  const tOver    = invoices.filter(i=>i.status!=="paid"&&i.dueDate&&i.dueDate<today()&&i.payTerms!=="due_receipt").length;

  if(loading) return <div style={{background:C.bg,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Segoe UI',sans-serif"}}><Dots text="Loading InvoiceAI Pro..." C={C}/></div>;

  return (
    <div style={{fontFamily:"'Segoe UI',sans-serif",background:C.bg,minHeight:"100vh",padding:"18px 14px 80px",color:C.text,transition:"background 0.2s"}}>
      <div style={{maxWidth:720,margin:"0 auto"}}>

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18}}>
          <div>
            <div style={{fontSize:"1.25rem",fontWeight:800,letterSpacing:"-0.02em"}}>
              Invoice<span style={{color:C.accent}}>AI</span>
              <span style={{fontSize:"0.48rem",background:C.accent,color:"#000",padding:"2px 7px",borderRadius:10,fontWeight:700,marginLeft:8,verticalAlign:"middle"}}>PRO</span>
            </div>
            <div style={{fontSize:"0.68rem",color:C.muted}}>Quotes · Invoices · Receipts · Contacts · Payments</div>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <button onClick={toggleTheme} style={{background:C.dim,border:"1px solid "+C.border,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:"0.72rem",fontWeight:700,color:C.text}}>
              {isDark?"☀️ Light":"🌙 Dark"}
            </button>
            <div style={{display:"flex",background:C.surface,border:"1px solid "+C.border,borderRadius:8,padding:3,gap:3}}>
              {["NGN","USD"].map(c=>(
                <button key={c} onClick={()=>setCurrency(c)} style={{padding:"4px 10px",borderRadius:6,border:"none",fontFamily:"inherit",fontSize:"0.68rem",fontWeight:700,cursor:"pointer",background:currency===c?C.accent:"transparent",color:currency===c?"#000":C.muted}}>
                  {c==="NGN"?"₦":"$"} {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        {invoices.length>0&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:16}}>
            {[["Invoiced",fStat(tInv),C.text],["Collected",fStat(tPaid),C.green],["Outstanding",fStat(tOut),C.amber],["Overdue",tOver+" items",C.red]].map(([l,v,col])=>(
              <div key={l} style={{background:C.card,border:"1px solid "+C.border,borderRadius:10,padding:"10px 12px"}}>
                <div style={{fontSize:"0.54rem",color:C.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{l}</div>
                <div style={{fontSize:"0.84rem",fontWeight:800,color:col}}>{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={{display:"flex",gap:4,background:C.surface,border:"1px solid "+C.border,borderRadius:10,padding:4,marginBottom:18}}>
          {[["new","➕ New"],["records","📁 Records"+(invoices.length>0?" ("+invoices.length+")":"")],["contacts","👥 Contacts"],["settings","⚙️ Settings"]].map(([id,label])=>(
            <button key={id} style={tabS(id)} onClick={()=>handleTabClick(id)}>{label}</button>
          ))}
        </div>

        {/* ══ NEW TAB ══ */}
        {tab==="new"&&(
          <>
            {newStep==="form"&&(
              <>
                {/* Doc type */}
                <div style={Card}>
                  <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
                    {[["invoice","🧾 Invoice"],["quote","📋 Quote"],["receipt","✅ Receipt"]].map(([type,label])=>(
                      <button key={type} onClick={()=>changeDocType(type)}
                        style={{padding:"8px 16px",borderRadius:8,border:"none",fontFamily:"inherit",fontWeight:700,fontSize:"0.78rem",cursor:"pointer",
                          background:docType===type?(type==="receipt"?C.green:C.accent):C.dim,
                          color:docType===type?"#fff":C.muted}}>
                        {label}
                      </button>
                    ))}
                    <div style={{flex:1}}/>
                    <div><span style={SLbl}>Doc No.</span><input style={{...inp(),width:128}} value={docNo} onChange={e=>setDocNo(e.target.value)}/></div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                    <div><span style={SLbl}>Issue Date</span><input type="date" style={inp()} value={issueDate} onChange={e=>setIssueDate(e.target.value)}/></div>
                    <div>
                      <span style={SLbl}>Payment Terms</span>
                      <select style={inp()} value={payTerms} onChange={e=>changeTerm(e.target.value)} disabled={docType==="receipt"}>
                        {TERMS.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                      </select>
                      <div style={{fontSize:"0.6rem",color:C.accent,marginTop:3,fontWeight:600}}>
                        ℹ️ {TERMS.find(t=>t.id===payTerms)?.note||""}
                      </div>
                    </div>
                    <div>
                      <span style={SLbl}>Due Date</span>
                      <input type="date" style={{...inp(),opacity:(payTerms==="due_receipt"||docType==="receipt")?0.5:1}} value={dueDate}
                        readOnly={payTerms==="due_receipt"||docType==="receipt"} onChange={e=>setDueDate(e.target.value)}/>
                      {(payTerms==="due_receipt"||docType==="receipt")&&<div style={{fontSize:"0.6rem",color:C.red,marginTop:3,fontWeight:700}}>⚡ Due on Receipt</div>}
                    </div>
                  </div>
                </div>

                {/* Client */}
                <div style={Card}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <span style={{...SLbl,marginBottom:0}}>👤 {docType==="receipt"?"Customer":"Client"}</span>
                    {selContact&&<span style={{fontSize:"0.62rem",background:C.accent+"22",color:C.accent,padding:"2px 8px",borderRadius:8,fontWeight:700}}>✓ Returning customer</span>}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                    <div style={{position:"relative"}}>
                      <span style={SLbl}>Name</span>
                      <input style={inp()} value={nameQuery} onChange={e=>handleNameChange(e.target.value)}
                        onFocus={()=>setShowSug(true)} onBlur={()=>setTimeout(()=>setShowSug(false),150)}
                        placeholder="Start typing name..." autoComplete="off"/>
                      {showSug&&contactSug.length>0&&(
                        <div style={{position:"absolute",top:"100%",left:0,right:0,background:C.card,border:"1px solid "+C.accent+"44",borderRadius:8,zIndex:100,marginTop:3,overflow:"hidden",boxShadow:"0 8px 24px #00000033"}}>
                          {contactSug.map(c=>(
                            <div key={c.id} onMouseDown={()=>selectSug(c)}
                              style={{padding:"9px 12px",cursor:"pointer",borderBottom:"1px solid "+C.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                              <div>
                                <div style={{fontSize:"0.8rem",fontWeight:700,color:C.text}}>{c.name}</div>
                                <div style={{fontSize:"0.66rem",color:C.muted}}>{[c.phone,c.email].filter(Boolean).join(" · ")}</div>
                              </div>
                              <span style={{fontSize:"0.6rem",color:C.accent,fontWeight:700}}>{c.invoiceCount||0} invoices</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div><span style={SLbl}>Email</span><input style={inp()} value={clientEmail} onChange={e=>setClientEmail(e.target.value)} placeholder="email@example.com"/></div>
                    <div><span style={SLbl}>Phone</span><input style={inp()} value={clientPhone} onChange={e=>setClientPhone(e.target.value)} placeholder="+234..."/></div>
                  </div>
                </div>

                {/* Payment method */}
                <div style={Card}>
                  <span style={SLbl}>💳 Payment Method</span>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {PAY_METHODS.map(m=>(
                      <button key={m} onClick={()=>setPaymentMethod(m)}
                        style={{padding:"6px 12px",borderRadius:8,border:"1px solid "+(paymentMethod===m?C.accent:C.border),background:paymentMethod===m?C.accent:C.surface,color:paymentMethod===m?"#000":C.muted,cursor:"pointer",fontFamily:"inherit",fontSize:"0.72rem",fontWeight:500}}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                {/* AI generator */}
                <div style={{...Card,border:"1px solid "+C.accent+"33"}}>
                  <div style={{fontSize:"0.6rem",fontWeight:700,color:C.accent,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>✦ AI Item Generator</div>
                  <textarea value={aiJob} onChange={e=>setAiJob(e.target.value)} rows={2}
                    placeholder={"e.g. iPhone 13 screen + battery replacement\nor: Logo design + social media kit for a restaurant"}
                    style={{...inp(),resize:"none",lineHeight:1.5,marginBottom:10}}/>
                  {aiError&&<ErrBox msg={aiError} C={C}/>}
                  <button onClick={generateFromAI} disabled={aiLoading}
                    style={{width:"100%",padding:"12px",borderRadius:10,border:"none",background:aiLoading?C.dim:C.accent,color:aiLoading?C.muted:"#000",fontFamily:"inherit",fontSize:"0.88rem",fontWeight:700,cursor:aiLoading?"not-allowed":"pointer"}}>
                    {aiLoading?"⏳ Generating...":"✦ Generate Items with AI"}
                  </button>
                  {aiLoading&&<Dots text={"Calculating "+currency+" pricing..."} C={C}/>}
                </div>

                {/* Line items */}
                <div style={Card}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                    <span style={{...SLbl,marginBottom:0}}>Line Items</span>
                    <button onClick={addItem} style={{padding:"5px 12px",borderRadius:7,border:"1px solid "+C.accent,background:"transparent",color:C.accent,fontFamily:"inherit",fontSize:"0.7rem",fontWeight:700,cursor:"pointer"}}>+ Add Item</button>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 52px 90px 90px 26px",gap:6,marginBottom:8}}>
                    {["Description","Qty","Rate","Amount",""].map((h,i)=><div key={i} style={{fontSize:"0.54rem",fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</div>)}
                  </div>
                  {items.map((item,idx)=>(
                    <div key={item.id} style={{display:"grid",gridTemplateColumns:"1fr 52px 90px 90px 26px",gap:6,marginBottom:7,alignItems:"center"}}>
                      <input value={item.desc} onChange={e=>updItem(item.id,"desc",e.target.value)} placeholder={"Item "+(idx+1)} style={inp({fontSize:"0.78rem"})}/>
                      <input type="number" min="0" value={item.qty} onChange={e=>updItem(item.id,"qty",e.target.value)} style={inp({textAlign:"center",fontSize:"0.78rem"})}/>
                      <input type="number" min="0" value={item.rate} onChange={e=>updItem(item.id,"rate",e.target.value)} placeholder="0.00" style={inp({fontSize:"0.78rem"})}/>
                      <div style={{fontSize:"0.78rem",fontWeight:700,color:C.text,textAlign:"right",padding:"9px 0"}}>{f(clamp(item.qty)*clamp(item.rate))}</div>
                      <button onClick={()=>removeItem(item.id)} disabled={items.length<=1} style={{background:"none",border:"none",color:items.length<=1?C.dim:C.muted,cursor:items.length<=1?"not-allowed":"pointer",fontSize:"1rem",padding:0,opacity:items.length<=1?0.3:1}}>×</button>
                    </div>
                  ))}
                  <div style={{height:1,background:C.border,margin:"12px 0"}}/>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                    <div><span style={SLbl}>Discount (%)</span><input type="number" min="0" max="100" value={discount} onChange={e=>setDiscount(e.target.value)} style={inp()} placeholder="0"/></div>
                    <div><span style={SLbl}>Tax ({settings.taxRate}%) <span style={{color:C.muted,fontSize:"0.54rem",fontWeight:400,textTransform:"none"}}>— edit in Settings</span></span>
                      <input readOnly value={settings.taxRate+"%"} style={{...inp(),opacity:0.5}}/>
                    </div>
                  </div>
                  <div style={{background:C.surface,borderRadius:10,padding:"12px 14px"}}>
                    {[["Subtotal",subtotal],discount>0?["Discount ("+discount+"%)",-discountAmt]:null,["Tax ("+settings.taxRate+"%)",taxAmt]].filter(Boolean).map(([l,v])=>(
                      <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:"0.76rem",color:C.muted,marginBottom:6}}>
                        <span>{l}</span><span style={{color:v<0?C.green:C.text}}>{v<0?"-"+f(Math.abs(v)):f(v)}</span>
                      </div>
                    ))}
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:"1rem",fontWeight:900,color:C.accent,borderTop:"1px solid "+C.border,paddingTop:10,marginTop:6}}>
                      <span>TOTAL</span><span>{f(total)}</span>
                    </div>
                  </div>
                </div>

                <div style={Card}>
                  <span style={SLbl}>Notes / Terms</span>
                  <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2} placeholder="e.g. 50% deposit required. Warranty: 30 days on parts." style={{...inp(),resize:"none",lineHeight:1.5}}/>
                </div>

                <div style={{display:"flex",gap:8}}>
                  <button onClick={handleSaveDraft} style={{flex:1,padding:"12px",borderRadius:10,border:"1px solid "+C.border,background:C.dim,color:C.muted,fontFamily:"inherit",fontSize:"0.86rem",fontWeight:700,cursor:"pointer"}}>Save Draft</button>
                  <button onClick={goToPreview} style={{flex:2,padding:"12px",borderRadius:10,border:"none",background:C.accent,color:"#000",fontFamily:"inherit",fontSize:"0.88rem",fontWeight:700,cursor:"pointer"}}>Preview →</button>
                </div>
              </>
            )}

            {newStep==="preview"&&previewInv&&(
              <>
                <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap"}}>
                  <button onClick={()=>setNewStep("form")} style={outlineBtn(C.muted)}>← Edit</button>
                  <button onClick={()=>copyWhatsApp(previewInv)} style={{...outlineBtn(C.accent),flex:1}}>📱 WhatsApp</button>
                  <button onClick={()=>openPrintWindow(previewInv,settings)} style={{...solidBtn(C.dim,C.muted)}}>📄 PDF</button>
                </div>
                <div style={{display:"flex",gap:6,marginBottom:14}}>
                  <button onClick={handleSaveAndSend} style={{...outlineBtn(C.accent),flex:1}}>✓ Save & Send</button>
                  <button onClick={handleSaveAndPaid} style={{...solidBtn(C.green),flex:1}}>💚 Paid & Send</button>
                </div>
                <div style={{background:C.surface,border:"1px solid "+C.accent+"22",borderRadius:10,padding:"8px 12px",marginBottom:12,fontSize:"0.72rem",color:C.muted}}>
                  📄 Click <strong style={{color:C.text}}>PDF</strong> → new window opens → Print → Save as PDF.
                </div>
                <InvPreview inv={previewInv} sett={settings} C={C}/>
              </>
            )}

            {newStep==="success"&&savedInv&&(
              <>
                <div style={{background:C.isDark?"#0a2218":"#f0fff4",border:"1px solid "+C.green+"44",borderRadius:14,padding:22,marginBottom:14,textAlign:"center"}}>
                  <div style={{fontSize:"2rem",marginBottom:8}}>✅</div>
                  <div style={{fontWeight:800,fontSize:"0.95rem",color:C.green,marginBottom:4}}>
                    {savedInv.status==="draft"?"Draft Saved!":savedInv.status==="paid"?"Paid & Saved! 💚":"Saved & Sent!"}
                  </div>
                  {/* FIX: use invoice's own currency formatter */}
                  <div style={{fontSize:"0.78rem",color:C.muted,marginBottom:3}}>{savedInv.docNo} · {savedInv.client?.name||"—"} · {savedF(savedInv.total)}</div>
                  <div style={{fontSize:"0.72rem",color:C.muted}}>Find it in <strong style={{color:C.accent}}>📁 Records</strong></div>
                </div>
                <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
                  <button onClick={()=>copyWhatsApp(savedInv)} style={{...outlineBtn(C.accent),flex:1}}>📱 WhatsApp</button>
                  <button onClick={()=>openPrintWindow(savedInv,settings)} style={{...solidBtn(C.dim,C.muted),flex:1}}>📄 PDF / Print</button>
                </div>
                <div style={{display:"flex",gap:8,marginBottom:14}}>
                  <button onClick={()=>handleTabClick("records")} style={{...outlineBtn(C.muted),flex:1}}>📁 View Records</button>
                  <button onClick={resetForNew} style={{...solidBtn(C.accent,"#000"),flex:2}}>➕ New</button>
                </div>
                <InvPreview inv={savedInv} sett={settings} C={C}/>
              </>
            )}
          </>
        )}

        {/* ══ RECORDS ══ */}
        {tab==="records"&&(
          <>
            {viewingInv?(
              <>
                <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
                  <button onClick={()=>setViewingInv(null)} style={outlineBtn(C.muted)}>← Back</button>
                  <button onClick={()=>copyWhatsApp(viewingInv)} style={{...outlineBtn(C.accent),flex:1}}>📱 WhatsApp</button>
                  <button onClick={()=>openPrintWindow(viewingInv,settings)} style={{...solidBtn(C.dim,C.muted),flex:1}}>📄 PDF</button>
                  <button onClick={()=>deleteInvoice(viewingInv.id)} style={{...solidBtn(C.isDark?"#2a1010":"#fff5f5",C.red),border:"1px solid "+C.red+"44"}}>Delete</button>
                </div>
                {viewingInv.status!=="draft"&&viewingInv.status!=="paid"&&(
                  <div style={{...Card,border:"1px solid "+C.accent+"33",marginBottom:14}}>
                    <div style={{fontSize:"0.6rem",fontWeight:700,color:C.accent,textTransform:"uppercase",marginBottom:10}}>+ Record Payment</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                      <div><span style={SLbl}>Amount</span><input type="number" min="0" value={payAmt} onChange={e=>setPayAmt(e.target.value)} style={inp()} placeholder="Enter amount..."/></div>
                      <div><span style={SLbl}>Method</span>
                        <select value={payMeth} onChange={e=>setPayMeth(e.target.value)} style={inp()}>
                          {PAY_METHODS.map(m=><option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                    </div>
                    <input value={payNote} onChange={e=>setPayNote(e.target.value)} style={{...inp(),marginBottom:10}} placeholder="Note (optional)..."/>
                    <div style={{display:"flex",alignItems:"center",gap:12}}>
                      <button onClick={()=>addPayment(viewingInv.id)} style={{...solidBtn(C.accent,"#000"),padding:"10px 20px"}}>Record Payment</button>
                      <span style={{fontSize:"0.7rem",color:C.muted}}>
                        Balance: {invFmt(viewingInv)(Math.max(0,(viewingInv.total||0)-(viewingInv.payments||[]).reduce((s,p)=>s+p.amount,0)))}
                      </span>
                    </div>
                    {(viewingInv.payments||[]).length>0&&(
                      <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid "+C.border}}>
                        <div style={{fontSize:"0.58rem",fontWeight:700,color:C.muted,textTransform:"uppercase",marginBottom:8}}>Payment History</div>
                        {viewingInv.payments.map((p,i)=>(
                          <div key={i} style={{display:"flex",justifyContent:"space-between",background:C.surface,borderRadius:7,padding:"7px 10px",marginBottom:5}}>
                            <span style={{fontSize:"0.76rem",fontWeight:700,color:C.green}}>{invFmt(viewingInv)(p.amount)}</span>
                            <span style={{fontSize:"0.68rem",color:C.muted}}>{p.method} · {fmtDate(p.date)}{p.note?" · "+p.note:""}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <InvPreview inv={viewingInv} sett={settings} C={C}/>
              </>
            ):(
              <>
                <div style={{display:"flex",gap:8,marginBottom:14}}>
                  <input value={recSearch} onChange={e=>setRecSearch(e.target.value)} placeholder="Search by client or invoice no..." style={{...inp(),flex:1}}/>
                  <select value={recFilter} onChange={e=>setRecFilter(e.target.value)} style={{...inp(),width:"auto"}}>
                    <option value="all">All</option>
                    {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                {filteredInv.length===0&&(
                  <div style={{textAlign:"center",padding:"40px 0",color:C.muted}}>
                    <div style={{fontSize:"2rem",marginBottom:10}}>📭</div>
                    <div style={{fontSize:"0.82rem"}}>No records yet. Create your first invoice!</div>
                  </div>
                )}
                {filteredInv.map(inv=>{
                  const fi=invFmt(inv);
                  const paid=(inv.payments||[]).reduce((s,p)=>s+p.amount,0);
                  const bal=(inv.total||0)-paid;
                  const isExp=expandedInv===inv.id;
                  return (
                    <div key={inv.id} style={{...Card,marginBottom:8}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}} onClick={()=>setExpandedInv(isExp?null:inv.id)}>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:4,flexWrap:"wrap"}}>
                            <span style={{fontWeight:700,fontSize:"0.86rem",color:C.text}}>{inv.docNo}</span>
                            <Tag status={inv.status}/>
                            <span style={{fontSize:"0.58rem",color:inv.type==="receipt"?C.green:C.muted,background:C.dim,padding:"1px 6px",borderRadius:6,fontWeight:inv.type==="receipt"?700:400}}>{inv.type}</span>
                            {inv.payTerms==="due_receipt"&&inv.type!=="receipt"&&<span style={{fontSize:"0.56rem",color:C.red,fontWeight:700}}>⚡ Due on Receipt</span>}
                            {inv.payTerms==="deposit_50"&&<span style={{fontSize:"0.56rem",color:C.amber,fontWeight:700}}>50% Deposit</span>}
                            {inv.payTerms==="installment2"&&<span style={{fontSize:"0.56rem",color:C.accent2,fontWeight:700}}>2x Install</span>}
                          </div>
                          <div style={{fontSize:"0.72rem",color:C.muted}}>{inv.client?.name||"—"} · {fmtDate(inv.issueDate)}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontWeight:800,color:C.accent,fontSize:"0.88rem"}}>{fi(inv.total)}</div>
                          {paid>0&&<div style={{fontSize:"0.62rem",color:C.green}}>Paid: {fi(paid)}</div>}
                          {bal>0&&inv.status!=="draft"&&<div style={{fontSize:"0.62rem",color:C.amber}}>Due: {fi(bal)}</div>}
                        </div>
                      </div>
                      {isExp&&(
                        <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid "+C.border}}>
                          <div style={{marginBottom:12}}>
                            <div style={{fontSize:"0.56rem",fontWeight:700,color:C.muted,textTransform:"uppercase",marginBottom:8}}>Update Status</div>
                            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                              {Object.entries(STATUS).map(([k,v])=>(
                                <button key={k} onClick={()=>updateStatus(inv.id,k)}
                                  style={{padding:"4px 10px",borderRadius:6,border:"1px solid "+(inv.status===k?v.color:C.border),background:inv.status===k?v.bg:"transparent",color:v.color,fontSize:"0.66rem",fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                                  {v.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div style={{display:"flex",gap:8}}>
                            <button onClick={()=>setViewingInv(inv)} style={outlineBtn(C.accent)}>View / Print</button>
                            <button onClick={()=>deleteInvoice(inv.id)} style={{...solidBtn(C.isDark?"#2a1010":"#fff5f5",C.red),border:"1px solid "+C.red+"44"}}>Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}

        {/* ══ CONTACTS ══ */}
        {tab==="contacts"&&(
          <>
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              <input value={contactSearch} onChange={e=>setContactSearch(e.target.value)} placeholder="Search contacts..." style={{...inp(),flex:1}}/>
              <button onClick={()=>setAddingContact(!addingContact)} style={{...solidBtn(C.accent,"#000"),padding:"8px 14px"}}>+ New</button>
            </div>
            {addingContact&&(
              <div style={{...Card,border:"1px solid "+C.accent+"44"}}>
                <div style={{fontSize:"0.6rem",fontWeight:700,color:C.accent,textTransform:"uppercase",marginBottom:12}}>New Contact</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div><span style={SLbl}>Name *</span><input style={inp()} value={newContact.name} onChange={e=>setNewContact(p=>({...p,name:e.target.value}))} placeholder="Full name"/></div>
                  <div><span style={SLbl}>Phone</span><input style={inp()} value={newContact.phone} onChange={e=>setNewContact(p=>({...p,phone:e.target.value}))} placeholder="+234..."/></div>
                  <div><span style={SLbl}>Email</span><input style={inp()} value={newContact.email} onChange={e=>setNewContact(p=>({...p,email:e.target.value}))} placeholder="email@example.com"/></div>
                  <div><span style={SLbl}>Address</span><input style={inp()} value={newContact.address} onChange={e=>setNewContact(p=>({...p,address:e.target.value}))} placeholder="City, Country"/></div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={addContact} style={{...solidBtn(C.accent,"#000"),padding:"7px 16px"}}>Save</button>
                  <button onClick={()=>setAddingContact(false)} style={{background:"transparent",border:"none",color:C.muted,fontFamily:"inherit",fontSize:"0.74rem",fontWeight:700,cursor:"pointer",padding:"7px 12px"}}>Cancel</button>
                </div>
              </div>
            )}
            {filteredContacts.length===0&&(
              <div style={{textAlign:"center",padding:"40px 0",color:C.muted}}>
                <div style={{fontSize:"2rem",marginBottom:10}}>👥</div>
                <div style={{fontSize:"0.82rem"}}>No contacts yet. Clients auto-save when you create invoices.</div>
              </div>
            )}
            {filteredContacts.map(c=>(
              <div key={c.id} style={Card}>
                {editContact?.id===c.id?(
                  <>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                      <div><span style={SLbl}>Name</span><input style={inp()} value={editContact.name} onChange={e=>setEditContact(p=>({...p,name:e.target.value}))}/></div>
                      <div><span style={SLbl}>Phone</span><input style={inp()} value={editContact.phone||""} onChange={e=>setEditContact(p=>({...p,phone:e.target.value}))}/></div>
                      <div><span style={SLbl}>Email</span><input style={inp()} value={editContact.email||""} onChange={e=>setEditContact(p=>({...p,email:e.target.value}))}/></div>
                      <div><span style={SLbl}>Address</span><input style={inp()} value={editContact.address||""} onChange={e=>setEditContact(p=>({...p,address:e.target.value}))}/></div>
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={saveContact} style={{...solidBtn(C.accent,"#000"),padding:"7px 16px"}}>Save</button>
                      <button onClick={()=>setEditContact(null)} style={{background:"transparent",border:"none",color:C.muted,fontFamily:"inherit",fontSize:"0.74rem",fontWeight:700,cursor:"pointer",padding:"7px"}}>Cancel</button>
                    </div>
                  </>
                ):(
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:"0.88rem",color:C.text,marginBottom:4}}>{c.name}</div>
                      <div style={{fontSize:"0.72rem",color:C.muted}}>{c.phone||"No phone"} · {c.email||"No email"}</div>
                      {c.address&&<div style={{fontSize:"0.68rem",color:C.muted}}>{c.address}</div>}
                      <div style={{display:"flex",gap:12,marginTop:6}}>
                        <span style={{fontSize:"0.66rem",color:C.accent}}>{c.invoiceCount||0} invoices</span>
                        <span style={{fontSize:"0.66rem",color:C.green}}>Total: {fmtNum(c.totalInvoiced||0,sym,locale)}</span>
                        {c.lastInvoice&&<span style={{fontSize:"0.66rem",color:C.muted}}>Last: {fmtDate(c.lastInvoice)}</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:5}}>
                      <button onClick={()=>{pickContact(c);handleTabClick("new");}} style={outlineBtn(C.accent)}>Invoice</button>
                      <button onClick={()=>setEditContact({...c})} style={{...solidBtn(C.dim,C.muted),padding:"5px 10px",fontSize:"0.66rem"}}>Edit</button>
                      <button onClick={()=>deleteContact(c.id)} style={{...solidBtn(C.isDark?"#2a1010":"#fff5f5",C.red),border:"1px solid "+C.red+"44",padding:"5px 8px",fontSize:"0.66rem"}}>×</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* ══ SETTINGS ══ */}
        {tab==="settings"&&(
          <>
            <div style={Card}>
              <div style={{fontSize:"0.6rem",fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:14}}>🏢 Business Profile</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div><span style={SLbl}>Business Name</span><input style={inp()} value={settings.bizName} onChange={e=>setSettings(p=>({...p,bizName:e.target.value}))} placeholder="JERRYTECH GLOBAL"/></div>
                <div><span style={SLbl}>Email</span><input style={inp()} value={settings.bizEmail} onChange={e=>setSettings(p=>({...p,bizEmail:e.target.value}))} placeholder="hello@jerrytech.com"/></div>
                <div><span style={SLbl}>Phone</span><input style={inp()} value={settings.bizPhone} onChange={e=>setSettings(p=>({...p,bizPhone:e.target.value}))} placeholder="+234..."/></div>
                <div><span style={SLbl}>Address</span><input style={inp()} value={settings.bizAddress} onChange={e=>setSettings(p=>({...p,bizAddress:e.target.value}))} placeholder="Ikorodu, Lagos"/></div>
              </div>
            </div>
            <div style={Card}>
              <div style={{fontSize:"0.6rem",fontWeight:700,color:C.accent2,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:14}}>🏦 Bank Details</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                <div><span style={SLbl}>Bank Name</span><input style={inp()} value={settings.bankName||""} onChange={e=>setSettings(p=>({...p,bankName:e.target.value}))} placeholder="Access Bank"/></div>
                <div><span style={SLbl}>Account Number</span><input style={inp()} value={settings.bankAccount||""} onChange={e=>setSettings(p=>({...p,bankAccount:e.target.value}))} placeholder="0123456789"/></div>
                <div><span style={SLbl}>Account Name</span><input style={inp()} value={settings.bankBranch||""} onChange={e=>setSettings(p=>({...p,bankBranch:e.target.value}))} placeholder="JERRYTECH GLOBAL"/></div>
              </div>
            </div>
            <div style={Card}>
              <div style={{fontSize:"0.6rem",fontWeight:700,color:C.amber,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:14}}>⚙️ Preferences</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                <div>
                  <span style={SLbl}>Tax Rate (%)</span>
                  <input type="number" min="0" max="100" style={inp()} value={settings.taxRate} onChange={e=>setSettings(p=>({...p,taxRate:parseFloat(e.target.value)||0}))} placeholder="7.5"/>
                  <div style={{fontSize:"0.6rem",color:C.muted,marginTop:3}}>Nigeria VAT = 7.5%</div>
                </div>
                <div>
                  <span style={SLbl}>Default Currency</span>
                  <select style={inp()} value={settings.currency||"NGN"} onChange={e=>{setSettings(p=>({...p,currency:e.target.value}));setCurrency(e.target.value);}}>
                    <option value="NGN">₦ Nigerian Naira</option>
                    <option value="USD">$ US Dollar</option>
                  </select>
                </div>
                <div>
                  <span style={SLbl}>App Theme</span>
                  <button onClick={toggleTheme} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid "+C.border,background:C.dim,color:C.text,fontFamily:"inherit",fontSize:"0.8rem",fontWeight:700,cursor:"pointer"}}>
                    {isDark?"☀️ Switch to Light":"🌙 Switch to Dark"}
                  </button>
                </div>
              </div>
            </div>

            {/* API Key for website deployment */}
            <div style={{...Card,border:"1px solid "+C.amber+"44",background:C.isDark?"#1a1500":"#fffbeb"}}>
              <div style={{fontSize:"0.6rem",fontWeight:700,color:C.amber,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>🔑 Anthropic API Key</div>
              <div style={{fontSize:"0.74rem",color:C.muted,marginBottom:10,lineHeight:1.6}}>
                Required when running on a website. Get yours at <strong style={{color:C.text}}>console.anthropic.com</strong> — $5 gives hundreds of invoices.
              </div>
              <input
                type="password"
                value={apiKey}
                onChange={e=>saveApiKey(e.target.value)}
                placeholder="sk-ant-api03-..."
                style={inp()}
              />
              {apiKey&&<div style={{fontSize:"0.66rem",color:C.green,marginTop:6,fontWeight:700}}>✓ API key saved — AI generation active</div>}
              {!apiKey&&<div style={{fontSize:"0.66rem",color:C.amber,marginTop:6}}>⚠ No key — AI item generation uses Claude artifact mode (works here, not on website)</div>}
            </div>

            <button onClick={async()=>{ await saveSettings(settings); showToast("Settings saved!"); }}
              style={{width:"100%",padding:"12px",borderRadius:10,border:"none",background:C.accent,color:"#000",fontFamily:"inherit",fontSize:"0.88rem",fontWeight:700,cursor:"pointer",marginBottom:16}}>
              Save All Settings
            </button>

            {/* Support */}
            <div style={{...Card,background:C.isDark?"#0a1020":"#EFF6FF",border:"1px solid "+C.accent2+"44"}}>
              <div style={{fontSize:"0.6rem",fontWeight:700,color:C.accent2,textTransform:"uppercase",marginBottom:12,letterSpacing:"0.1em"}}>🛠️ Support & Custom Development</div>
              <div style={{fontSize:"0.8rem",color:C.text,fontWeight:700,marginBottom:6}}>JERRYTECH Global Communications</div>
              <div style={{fontSize:"0.74rem",color:C.muted,lineHeight:1.8,marginBottom:12}}>
                Need custom features, white-label version, or system integration? Get in touch.
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[
                  ["💬 WhatsApp","+234 704 237 2858"],
                  ["📧 Email","automation.selfai.com"],
                  ["🌐 Website","selfai-hub.xyz"],
                  ["📍 Location","Ikorodu, Lagos, Nigeria"],
                ].map(([label,val])=>(
                  <div key={label} style={{background:C.surface,borderRadius:8,padding:"10px 12px",border:"1px solid "+C.border}}>
                    <div style={{fontSize:"0.6rem",color:C.muted,marginBottom:2}}>{label}</div>
                    <div style={{fontSize:"0.76rem",fontWeight:700,color:C.text}}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{marginTop:12,fontSize:"0.7rem",color:C.muted,textAlign:"center"}}>Built with ❤️ using AI · InvoiceAI Pro v5</div>
            </div>
          </>
        )}

      </div>

      {confirmDel&&<ConfirmModal item={confirmDel} C={C} onCancel={()=>setConfirmDel(null)} onConfirm={()=>confirmDel.type==="invoice"?doDeleteInvoice(confirmDel.id):doDeleteContact(confirmDel.id)}/>}
      {toast&&<Toast msg={toast} C={C} onClose={closeToast}/>}
      <style>{`
        @keyframes bounce{0%,80%,100%{transform:translateY(0);opacity:0.4}40%{transform:translateY(-8px);opacity:1}}
        input[type=date]::-webkit-calendar-picker-indicator{filter:${isDark?"invert(0.4)":"none"}}
        select option{background:${C.card};color:${C.text};}
      `}</style>
    </div>
  );
}